import React, { useMemo, useState } from 'react';
import { FlagIcon } from 'lucide-react';
import { budgetData, summaryData } from '../data/budgetData';
import { Condition, BudgetLineItem as BudgetLineItemType } from '../types/budget';
import { BudgetSummary } from '../components/BudgetSummary';
import { BudgetCategory } from '../components/BudgetCategory';
import { ConditionModal } from '../components/ConditionModal';
import { ConditionsPanel } from '../components/ConditionsPanel';
const TAB_LABELS: Record<string, string> = {
  all: 'All',
  'cat-soft-costs': 'Soft Costs',
  'cat-demolition': 'Demolition',
  'cat-foundation': 'Foundation',
  'cat-framing': 'Framing',
  'cat-plumbing': 'Plumbing',
  'cat-electric': 'Electric',
  'cat-interiors': 'Interiors',
  'cat-millwork': 'Millwork',
  'cat-flooring': 'Flooring',
  'cat-kitchen-baths': 'Kitchen & Baths',
  'cat-finishings': 'Finishings'
};
export function BudgetReview() {
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCondition, setEditingCondition] = useState<Condition | null>(null);
  const [selectedContext, setSelectedContext] = useState<{
    categoryId: string;
    categoryName: string;
    lineItem: BudgetLineItemType;
  } | null>(null);
  // Panel State
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  // Filter categories based on active tab
  const filteredCategories = useMemo(() => {
    if (activeTab === 'all') return budgetData;
    return budgetData.filter(c => c.id === activeTab);
  }, [activeTab]);
  // Condition counts per category
  const conditionCountByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    conditions.forEach(c => {
      counts[c.categoryId] = (counts[c.categoryId] || 0) + 1;
    });
    return counts;
  }, [conditions]);
  const handleAddConditionClick = (categoryId: string, categoryName: string, item: BudgetLineItemType) => {
    setSelectedContext({
      categoryId,
      categoryName,
      lineItem: item
    });
    setEditingCondition(null);
    setIsModalOpen(true);
  };
  const handleEditConditionClick = (condition: Condition) => {
    setEditingCondition(condition);
    setSelectedContext({
      categoryId: condition.categoryId,
      categoryName: condition.categoryName,
      lineItem: {
        id: condition.lineItemId,
        name: condition.lineItemName
      } as BudgetLineItemType
    });
    setIsModalOpen(true);
  };
  const handleSaveCondition = (note: string) => {
    if (editingCondition) {
      setConditions(prev => prev.map(c => c.id === editingCondition.id ? {
        ...c,
        note
      } : c));
    } else if (selectedContext) {
      const newCondition: Condition = {
        id: `cond-${Date.now()}`,
        lineItemId: selectedContext.lineItem.id,
        categoryId: selectedContext.categoryId,
        lineItemName: selectedContext.lineItem.name,
        categoryName: selectedContext.categoryName,
        note,
        priority: 'Medium',
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      setConditions(prev => [...prev, newCondition]);
      if (conditions.length === 0) {
        setIsPanelOpen(true);
      }
    }
  };
  const handleFinalizeReview = () => {
    setIsFinalized(true);
    setIsPanelOpen(true);
  };
  const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
  const tabKeys = ['all', ...budgetData.map(c => c.id)];
  return <div className="min-h-screen bg-white flex flex-col font-body" data-id="element-110">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm" data-id="element-111">
        <div className="px-4 h-16 flex items-center justify-between" data-id="element-112">
          <div data-id="element-113">
            <h1 className="text-xl font-heading font-light text-primary" data-id="element-114">
              Construction Budget Review
            </h1>
            <p className="text-xs font-body font-thin text-gray-500" data-id="element-115">
              Project: 123 Main St Development
            </p>
          </div>
          <button onClick={() => setIsPanelOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors relative" data-id="element-116">
            <FlagIcon className="w-4 h-4 text-accent-1" data-id="element-117" />
            <span className="text-sm font-body font-normal text-primary" data-id="element-118">
              Modifications ({conditions.length})
            </span>
            {conditions.length > 0 && <span className="absolute -top-2 -right-2 bg-accent text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-medium" data-id="element-119">
                {conditions.length}
              </span>}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full px-4 py-6 overflow-x-auto flex flex-col" data-id="element-120">
        <div className="min-w-[800px] flex flex-col flex-1" data-id="element-121">
          <BudgetSummary data={summaryData} data-id="element-122" />

          {/* Category Tab Bar */}
          <div className="border border-gray-300 border-b-0 bg-gray-50 overflow-x-auto" data-id="element-123">
            <div className="flex" data-id="element-124">
              {tabKeys.map(key => {
              const isActive = activeTab === key;
              const condCount = key === 'all' ? conditions.length : conditionCountByCategory[key] || 0;
              return <button key={key} onClick={() => setActiveTab(key)} className={`relative flex items-center gap-1.5 px-3 py-2 text-xs font-body whitespace-nowrap transition-colors border-b-2 ${isActive ? 'border-b-tertiary bg-white text-primary font-medium' : 'border-b-transparent text-gray-500 hover:text-primary hover:bg-gray-100'}`} data-id="element-125">
                    <span data-id="element-126">{TAB_LABELS[key] || key}</span>
                    {condCount > 0 && <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-accent-1 text-primary text-[10px] font-bold leading-none" data-id="element-127">
                        {condCount}
                      </span>}
                  </button>;
            })}
            </div>
          </div>

          {/* Budget Table */}
          <div className="flex-1 relative" data-id="element-128">
            <table className="w-full border-collapse border border-gray-300 text-xs font-body" style={{
            tableLayout: 'fixed'
          }} data-id="element-129">
              <colgroup data-id="element-130">
                <col style={{
                width: '50%'
              }} data-id="element-131" />
                <col style={{
                width: '15%'
              }} data-id="element-132" />
                <col style={{
                width: '15%'
              }} data-id="element-133" />
                <col style={{
                width: '20%'
              }} data-id="element-134" />
              </colgroup>
              <thead data-id="element-135">
                <tr data-id="element-136">
                  <td colSpan={4} className="h-4 bg-blue-800 border border-gray-300" data-id="element-137"></td>
                </tr>
                <tr className="bg-gray-100" data-id="element-138">
                  <th className="border border-gray-300 px-2 py-1 text-left font-normal" data-id="element-139"></th>
                  <th className="border border-gray-300 px-2 py-1 text-center font-normal" data-id="element-140">
                    % Of Budget
                  </th>
                  <th className="border border-gray-300 px-2 py-1 text-center font-normal" data-id="element-141">
                    Cost/SqFt
                  </th>
                  <th className="border border-gray-300 px-2 py-1 text-center font-bold" data-id="element-142">
                    BUDGET
                  </th>
                </tr>
                <tr data-id="element-143">
                  <td colSpan={4} className="border border-gray-300 h-1" data-id="element-144"></td>
                </tr>
              </thead>
              <tbody data-id="element-145">
                {filteredCategories.map(category => <BudgetCategory key={category.id} category={category} conditions={conditions} onAddCondition={item => handleAddConditionClick(category.id, category.name, item)} onEditCondition={handleEditConditionClick} data-id="element-146" />)}
              </tbody>
              {/* Sticky Grand Total Footer */}
              <tfoot className="sticky bottom-0 z-10" data-id="element-147">
                <tr className="bg-tertiary/40 font-bold text-primary" data-id="element-148">
                  <td className="border border-gray-300 px-2 py-1.5 text-xs font-body text-right" data-id="element-149">
                    100.0%
                  </td>
                  <td className="border border-gray-300 px-2 py-1.5 text-right text-xs font-body tabular-nums" data-id="element-150"></td>
                  <td className="border border-gray-300 px-2 py-1.5 text-right text-xs font-body tabular-nums" data-id="element-151"></td>
                  <td className="border border-gray-300 px-2 py-1.5 text-right text-xs font-body tabular-nums" data-id="element-152">
                    {formatCurrency(summaryData.totalBudget)}
                  </td>
                </tr>
                <tr data-id="element-153">
                  <td colSpan={4} className="h-4 bg-blue-800 border border-gray-300" data-id="element-154"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </main>

      {/* Modals & Panels */}
      <ConditionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveCondition} initialData={editingCondition} contextName={selectedContext ? `${selectedContext.categoryName} > ${selectedContext.lineItem.name}` : ''} data-id="element-155" />
      <ConditionsPanel isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)} conditions={conditions} onEditCondition={handleEditConditionClick} onFinalizeReview={handleFinalizeReview} data-id="element-156" />

      {/* Finalize Confirmation */}
      {isFinalized && <div className="fixed inset-0 z-50 flex items-center justify-center bg-primary/20 backdrop-blur-sm p-4" data-id="element-157">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden" data-id="element-158">
            <div className="p-6 border-b border-gray-100" data-id="element-159">
              <h2 className="text-lg font-heading font-light text-primary mb-1" data-id="element-160">
                Condition Generated
              </h2>
              <p className="text-xs font-body font-thin text-gray-500" data-id="element-161">
                The following condition has been created from{' '}
                {conditions.length} modification request
                {conditions.length !== 1 ? 's' : ''}.
              </p>
            </div>
            <div className="p-6 max-h-80 overflow-y-auto" data-id="element-162">
              <div className="bg-gray-50 border border-gray-200 rounded p-4 text-xs font-body space-y-3" data-id="element-163">
                <p className="font-medium text-primary" data-id="element-164">
                  Budget Modification Condition:
                </p>
                <p className="text-gray-600 font-thin" data-id="element-165">
                  The following line items require updates from the borrower:
                </p>
                <ul className="space-y-2" data-id="element-166">
                  {conditions.map((c, i) => <li key={c.id} className="border-l-2 border-accent pl-3" data-id="element-167">
                      <span className="font-medium text-primary" data-id="element-168">
                        {i + 1}. {c.lineItemName}
                      </span>
                      <span className="text-gray-400 text-[10px] ml-1" data-id="element-169">
                        ({c.categoryName})
                      </span>
                      <p className="text-gray-600 mt-0.5" data-id="element-170">{c.note}</p>
                    </li>)}
                </ul>
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-3" data-id="element-171">
              <button onClick={() => setIsFinalized(false)} className="px-4 py-2 text-sm font-body font-normal text-gray-600 hover:text-primary transition-colors" data-id="element-172">
                Back to Review
              </button>
              <button onClick={() => {
            setIsFinalized(false);
            setIsPanelOpen(false);
          }} className="px-4 py-2 text-sm font-body font-normal bg-tertiary text-white rounded-md hover:bg-tertiary/90 transition-colors" data-id="element-173">
                Confirm & Send to Borrower
              </button>
            </div>
          </div>
        </div>}
    </div>;
}